import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { createChart, ColorType } from 'lightweight-charts';
import { supabase, TABLES, type SolanaTokenData } from '../../utils/supabase';
import { useRealtimePrices, formatPriceChange } from '../../hooks/useRealtimePrices';
import { formatPriceDisplay } from '../../utils/priceFormatter';
import { getRelayTokenPriceSolana } from '../../hooks/useRelayPrice';
import {
  fetchPwaFeaturedSlots,
  createPwaFeaturedSlot,
  fetchPwaFeaturedCarousel,
  createPwaFeaturedCarousel,
  swapPwa,
  fetchPwaWalletBalance,
  fetchPwaTokenBalance,
  type PwaFeaturedSlots,
  type PwaCarouselSlots,
  type PwaTokenBalance,
  type PwaWalletBalance
} from '../../utils/telegramAuth';

const DEXSCREENER_ICON_URL = 'https://esjrycmiokijtxnbfyox.supabase.co/storage/v1/object/public/web-images/DEXS.png';
const ORCA_ICON_URL = 'https://esjrycmiokijtxnbfyox.supabase.co/storage/v1/object/public/web-images/ORCA-BUY.png';
const WEBSITE_ICON_URL = 'https://esjrycmiokijtxnbfyox.supabase.co/storage/v1/object/public/web-images/WEB.png';
const TWITTER_ICON_URL = 'https://esjrycmiokijtxnbfyox.supabase.co/storage/v1/object/public/web-images/X.png';
const TELEGRAM_ICON_URL = 'https://esjrycmiokijtxnbfyox.supabase.co/storage/v1/object/public/web-images/TG.png';
const VERYAI_ICON_URL = '/VeryAI.png';
const SOLANA_CHAIN_ID = 792703809;
const FEATURED_CONTACT_URL = (import.meta.env as any).VITE_FEATURED_TOKEN_CONTACT_URL || 'https://t.me/mavrkofficial';
const FEATURED_OVERRIDE_MINT = (import.meta.env as any).VITE_FEATURED_TOKEN_MINT || '';
const FEATURED_CAROUSEL_PRICE_SOL = Number((import.meta.env as any).VITE_FEATURED_CAROUSEL_SOL || '0.5');
const SENTRY_TOKEN_SYMBOL = 'SENTRY';
const APE_SETTINGS_KEY = 'pwa_ape_amount_sol';
const APE_SLIPPAGE_BPS = 50;
const SENTRY_TOKEN_SUPPLY = 1_000_000_000;

const normalizeWebsiteUrl = (url?: string | null): string | null => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `https://${url}`;
};

const normalizeTwitterUrl = (url?: string | null): string | null => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `https://twitter.com/${url.replace('@', '')}`;
};

const normalizeTelegramUrl = (url?: string | null): string | null => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `https://t.me/${url.replace('@', '')}`;
};

const formatMarketCapValue = (value: number): string => {
  if (value >= 1_000_000_000) return `$${(value / 1_000_000_000).toFixed(1)}B`;
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(1)}K`;
  return `$${value.toFixed(0)}`;
};

const formatMarketCapAxis = (value: number): string => {
  if (value >= 1_000_000_000) return `$${(value / 1_000_000_000).toFixed(1)}B`;
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(1)}K`;
  if (value >= 1) return `$${value.toFixed(2)}`;
  return `$${value.toFixed(4)}`;
};

const PageContainer = styled.div`
  padding: 16px;
  background: #000000;
  min-height: calc(100vh - 80px);
  width: 100%;
  padding-bottom: 100px;
  
  @media (max-width: 768px) {
    padding: 12px;
  }
`;

const PageTitle = styled.h1`
  font-size: 20px;
  margin: 0 0 12px 0;
  letter-spacing: 0.4px;
`;

const FeaturedGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 16px;
  margin-bottom: 16px;
  align-items: stretch;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const FeaturedCard = styled.div`
  background: linear-gradient(135deg, rgba(251, 191, 36, 0.15) 0%, rgba(8, 10, 14, 0.75) 100%);
  border: 1px solid transparent;
  border-radius: 18px;
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-bottom: 16px;
  backdrop-filter: blur(16px);
`;

const FeaturedMainCard = styled(FeaturedCard)`
  margin-bottom: 0;
  height: 100%;
`;

const FeaturedSideCard = styled.div`
  background: rgba(20, 20, 25, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 18px;
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: 100%;
`;

const FeaturedHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const FeaturedTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: #fbbf24;
`;

const FeaturedBody = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;

const FeaturedInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FeaturedName = styled.div`
  font-size: 16px;
  font-weight: 700;
`;

const FeaturedSymbol = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const FeaturedMetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.75);
`;

const FeaturedActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
`;

const FeaturedActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 10px;
  border-radius: 10px;
  border: 1px solid rgba(251, 191, 36, 0.45);
  background: rgba(251, 191, 36, 0.12);
  color: #fbbf24;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  cursor: pointer;
`;

const FeaturedSocialRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ScrollingFeedContainer = styled.div`
  width: 100%;
  overflow: hidden;
  background: rgba(20, 20, 25, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 16px;
  padding: 14px 0;
  margin-bottom: 16px;
`;

const ScrollingFeedContainerCompact = styled(ScrollingFeedContainer)`
  margin-bottom: 0;
`;

const ScrollingFeedTrack = styled.div`
  display: flex;
  animation: scroll 60s linear infinite;
  width: fit-content;

  &:hover {
    animation-play-state: paused;
  }

  @keyframes scroll {
    0% {
      transform: translateX(0);
    }
    100% {
      transform: translateX(-50%);
    }
  }
`;

const ScrollingTokenCard = styled.div<{ $featured?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 18px;
  margin: 0 8px;
  background: ${({ $featured }) =>
    $featured
      ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.18) 0%, rgba(8, 10, 14, 0.85) 100%)'
      : 'rgba(255, 255, 255, 0.03)'};
  border: 1px solid ${({ $featured }) => ($featured ? 'rgba(16, 185, 129, 0.25)' : 'rgba(255, 255, 255, 0.06)')};
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  flex-shrink: 0;
  backdrop-filter: ${({ $featured }) => ($featured ? 'blur(12px)' : 'none')};

  &:hover {
    background: ${({ $featured }) =>
      $featured ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.28) 0%, rgba(8, 10, 14, 0.9) 100%)' : '#000000'};
    border-color: ${({ $featured }) => ($featured ? 'rgba(16, 185, 129, 0.5)' : '#333333')};
    transform: translateY(-2px);
  }
`;

const ScrollingTokenLogo = styled.img`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  object-fit: cover;
  background: #000000;
`;

const ScrollingTokenInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ScrollingTokenSymbol = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #ffffff;
`;

const ScrollingTokenMarketCap = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #10b981;
  margin-left: auto;
`;

const SecondaryFeatureRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 16px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`;

const SecondaryFeatureCard = styled.button`
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.18) 0%, rgba(8, 10, 14, 0.85) 100%);
  border: 1px solid rgba(16, 185, 129, 0.25);
  border-radius: 14px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  backdrop-filter: blur(12px);
`;

const SecondaryFeatureSymbol = styled.div`
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #fbbf24;
`;

const SlotPickerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const SlotPickerPrimary = styled.button<{ $active?: boolean }>`
  background: linear-gradient(135deg, rgba(251, 191, 36, 0.18) 0%, rgba(8, 10, 14, 0.8) 100%);
  border: 1px solid ${({ $active }) => ($active ? 'rgba(251, 191, 36, 0.7)' : 'rgba(255, 255, 255, 0.08)')};
  border-radius: 16px;
  padding: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  backdrop-filter: blur(14px);
  cursor: pointer;
`;

const SlotPickerSecondaryRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
`;

const SlotPickerSecondary = styled.button<{ $active?: boolean }>`
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(8, 10, 14, 0.85) 100%);
  border: 1px solid ${({ $active }) => ($active ? 'rgba(16, 185, 129, 0.7)' : 'rgba(255, 255, 255, 0.08)')};
  border-radius: 14px;
  padding: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  backdrop-filter: blur(12px);
  cursor: pointer;
`;

const SlotPickerLabel = styled.div`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #fbbf24;
  font-weight: 700;
`;

const SelectedTokenRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.04);
`;

const SlotPlaceholder = styled.div`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #000000;
  border: 1px solid rgba(255, 255, 255, 0.08);
`;

const ModalBackdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
`;

const ModalCard = styled.div`
  background: #0b0b10;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 18px;
  padding: 18px;
  width: 100%;
  max-width: 420px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ChartModalCard = styled(ModalCard)`
  max-width: 520px;
  gap: 14px;
`;

const ChartHeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ChartTokenLogo = styled.img`
  width: 46px;
  height: 46px;
  border-radius: 12px;
  object-fit: cover;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.08);
`;

const ChartTokenInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ChartTokenName = styled.div`
  font-size: 16px;
  font-weight: 700;
`;

const ChartTokenSymbol = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ChartFrameWrapper = styled.div`
  width: 100%;
  height: 320px;
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: #000000;
  position: relative;
`;

const ChartCanvas = styled.div`
  width: 100%;
  height: 100%;
`;

const ChartPlaceholder = styled.div`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.6);
  font-size: 12px;
  background: rgba(0, 0, 0, 0.4);
`;

const ChartToggleRow = styled.div`
  display: flex;
  gap: 10px;
`;

const ChartTimeframeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const ChartTimeframeButton = styled.button<{ $active?: boolean }>`
  padding: 6px 10px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: ${({ $active }) => ($active ? 'rgba(168, 85, 247, 0.2)' : 'rgba(255, 255, 255, 0.04)')};
  color: ${({ $active }) => ($active ? '#c4b5fd' : 'rgba(255, 255, 255, 0.7)')};
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  cursor: pointer;
`;

const ChartToggleButton = styled.button<{ $active?: boolean; $variant?: 'sol' | 'token' }>`
  flex: 1;
  padding: 7px 10px;
  border-radius: 12px;
  border: 1px solid
    ${({ $variant }) =>
      $variant === 'token' ? 'rgba(239, 68, 68, 0.6)' : 'rgba(255, 255, 255, 0.12)'};
  background: ${({ $active, $variant }) => {
    if (!$active) return 'rgba(255, 255, 255, 0.04)';
    return $variant === 'token' ? 'rgba(239, 68, 68, 0.2)' : 'rgba(16, 185, 129, 0.18)';
  }};
  color: ${({ $active, $variant }) => {
    if (!$active) return 'rgba(255, 255, 255, 0.7)';
    return $variant === 'token' ? '#f87171' : '#10b981';
  }};
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  cursor: pointer;
`;

const ChartInputRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const ChartInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.04);
  color: #ffffff;
  font-size: 14px;
`;

const ChartBalanceRow = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.65);
`;

const ChartActionsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
`;

const ChartActionButton = styled.button<{ $disabled?: boolean; $variant?: 'buy' | 'sell' }>`
  padding: 9px 10px;
  border-radius: 12px;
  border: 1px solid
    ${({ $variant }) =>
      $variant === 'sell' ? 'rgba(239, 68, 68, 0.6)' : 'rgba(16, 185, 129, 0.6)'};
  background: ${({ $disabled, $variant }) => {
    if ($disabled) return 'rgba(255, 255, 255, 0.05)';
    return $variant === 'sell' ? 'rgba(239, 68, 68, 0.18)' : 'rgba(16, 185, 129, 0.18)';
  }};
  color: ${({ $disabled, $variant }) => {
    if ($disabled) return 'rgba(255, 255, 255, 0.4)';
    return $variant === 'sell' ? '#f87171' : '#10b981';
  }};
  font-weight: 700;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ModalTitle = styled.div`
  font-size: 16px;
  font-weight: 700;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  font-size: 18px;
  cursor: pointer;
`;

const ModalSearchInput = styled.input`
  padding: 10px 12px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.04);
  color: #ffffff;
  font-size: 14px;
`;

const ModalTokenList = styled.div`
  max-height: 220px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ModalTokenButton = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px;
  border-radius: 12px;
  border: 1px solid ${({ $active }) => ($active ? 'rgba(16, 185, 129, 0.6)' : 'rgba(255, 255, 255, 0.08)')};
  background: ${({ $active }) => ($active ? 'rgba(16, 185, 129, 0.12)' : 'rgba(255, 255, 255, 0.04)')};
  color: #ffffff;
  text-align: left;
  cursor: pointer;
`;

const ModalTokenInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const SlotOptionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const SlotOptionButton = styled.button<{ $active?: boolean }>`
  padding: 8px 12px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: ${({ $active }) => ($active ? 'rgba(16, 185, 129, 0.18)' : 'transparent')};
  color: ${({ $active }) => ($active ? '#10b981' : 'rgba(255, 255, 255, 0.7)')};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
`;

const PayButton = styled.button`
  padding: 12px;
  border-radius: 12px;
  border: 1px solid rgba(16, 185, 129, 0.6);
  background: rgba(16, 185, 129, 0.15);
  color: #10b981;
  font-weight: 700;
  cursor: pointer;
`;

const SearchRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 12px 16px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.04);
  color: #ffffff;
  font-size: 14px;
`;

const TokenGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const TokenCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  background: rgba(20, 20, 25, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  padding: 16px;
  color: inherit;
  cursor: pointer;
  transition: border-color 0.2s ease, transform 0.2s ease;

  &:hover {
    border-color: rgba(168, 85, 247, 0.6);
  }
`;

const TokenLogo = styled.img`
  width: 52px;
  height: 52px;
  border-radius: 12px;
  object-fit: cover;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.08);
`;

const TokenHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const TokenInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const TokenName = styled.div`
  font-size: 15px;
  font-weight: 600;
`;

const TokenSymbol = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
  text-transform: uppercase;
  letter-spacing: 0.6px;
`;

const TokenCreator = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
`;

const TokenStatsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;

const TokenStat = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const TokenStatLabel = styled.div`
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: rgba(255, 255, 255, 0.5);
`;

const TokenStatValue = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #ffffff;
`;

const PriceChange = styled.div<{ $positive?: boolean }>`
  font-size: 11px;
  font-weight: 600;
  color: ${(props) => (props.$positive ? '#10b981' : '#ef4444')};
`;

const LinksRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const TokenLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-1px);
  }
`;

const TokenLinkIcon = styled.img`
  width: 26px;
  height: 26px;
  object-fit: contain;
`;

const TradeButton = styled.button<{ $disabled?: boolean }>`
  padding: 8px 14px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: ${({ $disabled }) => ($disabled ? 'rgba(255, 255, 255, 0.05)' : 'linear-gradient(135deg, #14F195 0%, #10b981 100%)')};
  color: ${({ $disabled }) => ($disabled ? 'rgba(255, 255, 255, 0.4)' : '#000000')};
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  min-width: 72px;
  text-align: center;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  transition: all 0.2s ease;

  &:hover {
    ${({ $disabled }) => (!$disabled ? 'transform: translateY(-1px); box-shadow: 0 4px 12px rgba(20, 241, 149, 0.3);' : '')}
  }
`;

const ApeButton = styled.button<{ $disabled?: boolean }>`
  padding: 8px 14px;
  border-radius: 10px;
  border: 1px solid rgba(251, 191, 36, 0.5);
  background: ${({ $disabled }) =>
    $disabled ? 'rgba(255, 255, 255, 0.05)' : 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)'};
  color: ${({ $disabled }) => ($disabled ? 'rgba(255, 255, 255, 0.4)' : '#000000')};
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  min-width: 72px;
  text-align: center;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  transition: all 0.2s ease;

  &:hover {
    ${({ $disabled }) => (!$disabled ? 'transform: translateY(-1px); box-shadow: 0 4px 12px rgba(251, 191, 36, 0.35);' : '')}
  }
`;

const TokenActionColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: flex-end;
`;

const ApeStatusText = styled.div<{ $error?: boolean }>`
  font-size: 11px;
  color: ${({ $error }) => ($error ? '#f87171' : 'rgba(255, 255, 255, 0.6)')};
`;

const HelperText = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
`;

const AgentNotice = styled.div`
  margin-top: 10px;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.6);
  display: flex;
  align-items: center;
  gap: 6px;
`;

const AgentNoticeIcon = styled.img`
  width: 14px;
  height: 14px;
  object-fit: contain;
`;

interface SentryTokensPWAProps {
  basePath?: string;
  tokenClickMode?: 'swap' | 'chart';
  showPageTitle?: boolean;
  featuredLayout?: 'stacked' | 'split';
}

const SentryTokensPWA: React.FC<SentryTokensPWAProps> = ({
  basePath = '/sentry-pwa',
  tokenClickMode = 'swap',
  showPageTitle = true,
  featuredLayout = 'stacked'
}) => {
  const [tokens, setTokens] = useState<SolanaTokenData[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [featuredSlots, setFeaturedSlots] = useState<PwaFeaturedSlots | null>(null);
  const [carouselSlots, setCarouselSlots] = useState<PwaCarouselSlots | null>(null);
  const [featureModalOpen, setFeatureModalOpen] = useState(false);
  const [featureSearch, setFeatureSearch] = useState('');
  const [featureToken, setFeatureToken] = useState<SolanaTokenData | null>(null);
  const [featureSlotType, setFeatureSlotType] = useState<'primary' | 'secondary_1' | 'secondary_2' | 'secondary_3'>('primary');
  const [featureDuration, setFeatureDuration] = useState<4 | 8 | 12 | 24>(4);
  const [featureStatus, setFeatureStatus] = useState<string | null>(null);
  const [featureLoading, setFeatureLoading] = useState(false);
  const [carouselLoading, setCarouselLoading] = useState(false);
  const [apeAmountSol, setApeAmountSol] = useState<number | null>(null);
  const [apeLoadingByMint, setApeLoadingByMint] = useState<Record<string, boolean>>({});
  const [apeStatusByMint, setApeStatusByMint] = useState<Record<string, {
    message: string;
    error?: boolean;
    signature?: string;
    amountOut?: number | null;
    symbol?: string | null;
  }>>({});
  const [chartModalOpen, setChartModalOpen] = useState(false);
  const [chartToken, setChartToken] = useState<SolanaTokenData | null>(null);
  const [chartSide, setChartSide] = useState<'sol' | 'token'>('sol');
  const [chartAmount, setChartAmount] = useState('');
  const [chartStatus, setChartStatus] = useState<string | null>(null);
  const [chartLoading, setChartLoading] = useState(false);
  const [chartWalletBalance, setChartWalletBalance] = useState<PwaWalletBalance | null>(null);
  const [chartTokenBalance, setChartTokenBalance] = useState<PwaTokenBalance | null>(null);
  const [chartDataStatus, setChartDataStatus] = useState<string | null>(null);
  const [chartSolPriceUsd, setChartSolPriceUsd] = useState<number | null>(null);
  const [chartTokenPriceUsd, setChartTokenPriceUsd] = useState<number | null>(null);
  const [chartTimeframe, setChartTimeframe] = useState<'1m' | '5m' | '15m' | '30m' | '1h' | '4h' | '1d'>('5m');
  const chartContainerRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  const { getPriceForToken, get24hChangeForToken } = useRealtimePrices(SOLANA_CHAIN_ID);

  useEffect(() => {
    let cancelled = false;

    const loadTokens = async () => {
      setLoading(true);
      setError(null);
      try {
        const [solTokens, agentTokens] = await Promise.all([
          supabase
            .from(TABLES.TOKENS_SOL)
            .select('*')
            .order('created_at', { ascending: false }),
          supabase
            .from(TABLES.TOKENS_SOL_AGENTS)
            .select('*')
            .order('created_at', { ascending: false })
        ]);

        if (solTokens.error) {
          throw solTokens.error;
        }
        if (agentTokens.error) {
          throw agentTokens.error;
        }

        if (!cancelled) {
          const mappedAgents = (agentTokens.data || []).map((token) => ({
            ...token,
            creator_username: token.agent_name || token.creator_username || token.moltbook_handle || 'Molting_CMI',
            is_agent: true
          })) as SolanaTokenData[];
          const merged = [...(solTokens.data || []), ...mappedAgents].sort((a, b) => {
            const aTime = a.created_at ? new Date(a.created_at).getTime() : 0;
            const bTime = b.created_at ? new Date(b.created_at).getTime() : 0;
            return bTime - aTime;
          });
          setTokens(merged);
        }
      } catch (loadError: any) {
        if (!cancelled) {
          setError(loadError?.message || 'Unable to load tokens.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadTokens();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadFeaturedSlots = async () => {
      try {
        const data = await fetchPwaFeaturedSlots();
        if (!cancelled) {
          setFeaturedSlots(data);
        }
      } catch (loadError) {
        if (!cancelled) {
          setFeaturedSlots(null);
        }
      }
    };
    loadFeaturedSlots();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadCarouselSlots = async () => {
      try {
        const data = await fetchPwaFeaturedCarousel();
        if (!cancelled) {
          setCarouselSlots(data);
        }
      } catch {
        if (!cancelled) {
          setCarouselSlots(null);
        }
      }
    };
    loadCarouselSlots();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const stored = window.localStorage.getItem(APE_SETTINGS_KEY);
    if (stored) {
      const numeric = Number(stored);
      if (Number.isFinite(numeric) && numeric > 0) {
        setApeAmountSol(numeric);
      }
    }
    const handleStorage = (event: StorageEvent) => {
      if (event.key !== APE_SETTINGS_KEY) return;
      const numeric = Number(event.newValue || '');
      if (Number.isFinite(numeric) && numeric > 0) {
        setApeAmountSol(numeric);
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const filteredTokens = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return tokens;
    return tokens.filter((token) =>
      token.name?.toLowerCase().includes(query) ||
      token.symbol?.toLowerCase().includes(query) ||
      token.mint_address?.toLowerCase().includes(query)
    );
  }, [tokens, search]);

  const featureSearchResults = useMemo(() => {
    const query = featureSearch.trim().toLowerCase();
    if (!query) return tokens.slice(0, 10);
    return tokens.filter((token) =>
      token.name?.toLowerCase().includes(query) ||
      token.symbol?.toLowerCase().includes(query) ||
      token.mint_address?.toLowerCase().includes(query)
    ).slice(0, 20);
  }, [tokens, featureSearch]);

  const handleTokenTrade = (token: SolanaTokenData) => {
    if (!token.mint_address) return;
    navigate(`${basePath}/swap?mint=${token.mint_address}`);
  };

  const handleTokenClick = (token: SolanaTokenData) => {
    if (!token.mint_address) return;
    if (tokenClickMode === 'chart') {
      handleOpenChart(token);
      return;
    }
    handleTokenTrade(token);
  };

  const handleOpenChart = (token: SolanaTokenData) => {
    setChartToken(token);
    setChartModalOpen(true);
    setChartSide('sol');
    setChartTimeframe('5m');
    setChartAmount('');
    setChartStatus(null);
    setChartDataStatus(null);
    setChartTokenBalance(null);
  };

  const handleChartAmountChange = (value: string) => {
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setChartAmount(value);
    }
  };

  useEffect(() => {
    if (!chartModalOpen || !chartToken) return;
    let cancelled = false;
    const loadBalances = async () => {
      try {
        const balance = await fetchPwaWalletBalance();
        if (!cancelled) {
          setChartWalletBalance(balance);
        }
      } catch {
        if (!cancelled) {
          setChartWalletBalance(null);
        }
      }
      if (!chartToken.mint_address) return;
      try {
        const tokenBalance = await fetchPwaTokenBalance(chartToken.mint_address);
        if (!cancelled) {
          setChartTokenBalance(tokenBalance);
        }
      } catch {
        if (!cancelled) {
          setChartTokenBalance(null);
        }
      }

      try {
        const [solPrice, tokenPrice] = await Promise.all([
          getRelayTokenPriceSolana('So11111111111111111111111111111111111111112'),
          chartToken.mint_address ? getRelayTokenPriceSolana(chartToken.mint_address) : Promise.resolve(null)
        ]);
        if (!cancelled) {
          setChartSolPriceUsd(solPrice || null);
          setChartTokenPriceUsd(tokenPrice || null);
        }
      } catch {
        if (!cancelled) {
          setChartSolPriceUsd(null);
          setChartTokenPriceUsd(null);
        }
      }
    };
    loadBalances();
    return () => {
      cancelled = true;
    };
  }, [chartModalOpen, chartToken]);

  const handleChartSwap = async (direction: 'buy' | 'sell') => {
    if (!chartToken?.pool_address) {
      setChartStatus('No Orca pool found for this token.');
      return;
    }
    if (direction === 'buy' && chartSide !== 'sol') {
      setChartStatus('Select SOL to buy.');
      return;
    }
    if (direction === 'sell' && chartSide !== 'token') {
      setChartStatus('Select token to sell.');
      return;
    }
    const numericAmount = Number(chartAmount);
    if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
      setChartStatus('Enter a valid amount.');
      return;
    }
    setChartLoading(true);
    setChartStatus(null);
    try {
      const tokenDecimals = chartToken.decimals ?? 9;
      const amountInBase = direction === 'buy'
        ? Math.floor(numericAmount * 1e9)
        : Math.floor(numericAmount * Math.pow(10, tokenDecimals));
      await swapPwa({
        direction,
        mintAddress: chartToken.mint_address,
        poolAddress: chartToken.pool_address,
        amountIn: String(amountInBase),
        minAmountOut: '0'
      });
      setChartStatus('Success!');
      setChartAmount('');
      const balance = await fetchPwaWalletBalance();
      setChartWalletBalance(balance);
      if (chartToken.mint_address) {
        const tokenBalance = await fetchPwaTokenBalance(chartToken.mint_address);
        setChartTokenBalance(tokenBalance);
      }
    } catch (swapError: any) {
      setChartStatus(swapError?.message || 'Swap failed.');
    } finally {
      setChartLoading(false);
    }
  };

  useEffect(() => {
    if (!chartModalOpen || !chartToken?.pool_address || !chartContainerRef.current) return;
    let cancelled = false;
    let resizeObserver: ResizeObserver | null = null;
    let chart: ReturnType<typeof createChart> | null = null;
    let candlestickSeries: ReturnType<typeof chart.addCandlestickSeries> | null = null;
    let volumeSeries: ReturnType<typeof chart.addHistogramSeries> | null = null;

    const loadChart = async () => {
      setChartDataStatus('Loading chart…');
      try {
        const timeframeConfig = (() => {
          switch (chartTimeframe) {
            case '1m':
              return { timeframe: 'minute', aggregate: '1' };
            case '5m':
              return { timeframe: 'minute', aggregate: '5' };
            case '15m':
              return { timeframe: 'minute', aggregate: '15' };
            case '30m':
              return { timeframe: 'minute', aggregate: '15', resampleSeconds: 1800 };
            case '1h':
              return { timeframe: 'hour', aggregate: '1' };
            case '4h':
              return { timeframe: 'hour', aggregate: '4' };
            case '1d':
              return { timeframe: 'day', aggregate: '1' };
            default:
              return { timeframe: 'minute', aggregate: '5' };
          }
        })();
        const response = await fetch(
          `https://api.geckoterminal.com/api/v2/networks/solana/pools/${chartToken.pool_address}/ohlcv/${timeframeConfig.timeframe}?aggregate=${timeframeConfig.aggregate}&limit=200&currency=usd`,
          {
            headers: {
              Accept: 'application/json;version=20230203'
            }
          }
        );
        if (!response.ok) {
          throw new Error('Unable to load chart data.');
        }
        const payload = await response.json();
        const rawOhlcv: number[][] = payload?.data?.attributes?.ohlcv_list || [];
        const ohlcvList = rawOhlcv
          .filter((item) =>
            Array.isArray(item) &&
            item.length >= 6 &&
            item[0] != null &&
            item[1] != null &&
            item[2] != null &&
            item[3] != null &&
            item[4] != null &&
            item[5] != null
          )
          .map((item) => ({
            time: Number(item[0]),
            open: Number(item[1]),
            high: Number(item[2]),
            low: Number(item[3]),
            close: Number(item[4]),
            volume: Number(item[5])
          }))
          .filter((item) =>
            Number.isFinite(item.time) &&
            Number.isFinite(item.open) &&
            Number.isFinite(item.high) &&
            Number.isFinite(item.low) &&
            Number.isFinite(item.close) &&
            Number.isFinite(item.volume)
          )
          .sort((a, b) => a.time - b.time);
        const resampled = timeframeConfig.resampleSeconds
          ? (() => {
              const bucketed = new Map<number, typeof ohlcvList[number]>();
              ohlcvList.forEach((item) => {
                const bucket = Math.floor(item.time / timeframeConfig.resampleSeconds) * timeframeConfig.resampleSeconds;
                const existing = bucketed.get(bucket);
                if (!existing) {
                  bucketed.set(bucket, { ...item, time: bucket });
                  return;
                }
                existing.high = Math.max(existing.high, item.high);
                existing.low = Math.min(existing.low, item.low);
                existing.close = item.close;
                existing.volume += item.volume;
              });
              return Array.from(bucketed.values()).sort((a, b) => a.time - b.time);
            })()
          : ohlcvList;
        if (!resampled.length) {
          setChartDataStatus('No chart data available.');
          return;
        }
        if (cancelled || !chartContainerRef.current) return;
        chart = createChart(chartContainerRef.current, {
          layout: {
            background: { type: ColorType.Solid, color: '#000000' },
            textColor: '#cbd5f5'
          },
          rightPriceScale: {
            borderColor: 'rgba(255, 255, 255, 0.1)'
          },
          timeScale: {
            borderColor: 'rgba(255, 255, 255, 0.1)'
          },
          grid: {
            vertLines: { color: 'rgba(255, 255, 255, 0.05)' },
            horzLines: { color: 'rgba(255, 255, 255, 0.05)' }
          },
          height: chartContainerRef.current.clientHeight,
          width: chartContainerRef.current.clientWidth
        });
        candlestickSeries = chart.addCandlestickSeries({
          upColor: '#10b981',
          downColor: '#ef4444',
          wickUpColor: '#10b981',
          wickDownColor: '#ef4444',
          borderVisible: false,
          priceFormat: {
            type: 'custom',
            formatter: (price) => formatMarketCapAxis(price)
          }
        });
        volumeSeries = chart.addHistogramSeries({
          priceScaleId: '',
          priceFormat: { type: 'volume' },
          color: 'rgba(16, 185, 129, 0.5)'
        });
        chart.priceScale('').applyOptions({
          scaleMargins: {
            top: 0.8,
            bottom: 0
          }
        });
        const candles = resampled.map((item) => ({
          time: item.time,
          open: item.open * SENTRY_TOKEN_SUPPLY,
          high: item.high * SENTRY_TOKEN_SUPPLY,
          low: item.low * SENTRY_TOKEN_SUPPLY,
          close: item.close * SENTRY_TOKEN_SUPPLY
        }));
        const volumes = resampled.map((item) => ({
          time: item.time,
          value: item.volume,
          color: item.close >= item.open ? 'rgba(16, 185, 129, 0.5)' : 'rgba(239, 68, 68, 0.5)'
        }));
        candlestickSeries.setData(candles);
        volumeSeries.setData(volumes);
        setChartDataStatus(null);

        resizeObserver = new ResizeObserver(() => {
          if (!chartContainerRef.current || !chart) return;
          chart.applyOptions({
            width: chartContainerRef.current.clientWidth,
            height: chartContainerRef.current.clientHeight
          });
        });
        resizeObserver.observe(chartContainerRef.current);
      } catch (error: any) {
        setChartDataStatus(error?.message || 'Unable to load chart data.');
      }
    };

    loadChart();

    return () => {
      cancelled = true;
      if (resizeObserver && chartContainerRef.current) {
        resizeObserver.unobserve(chartContainerRef.current);
      }
      if (chart) {
        chart.remove();
      }
    };
  }, [chartModalOpen, chartToken, chartTimeframe]);

  const handleApeClick = async (token: SolanaTokenData) => {
    const mint = token.mint_address;
    if (!mint || !token.pool_address) {
      setApeStatusByMint((prev) => ({
        ...prev,
        [mint || 'unknown']: { message: 'No Orca pool found for this token.', error: true }
      }));
      return;
    }
    let effectiveApeAmount = apeAmountSol;
    if ((!effectiveApeAmount || effectiveApeAmount <= 0) && typeof window !== 'undefined') {
      const stored = window.localStorage.getItem(APE_SETTINGS_KEY);
      const numeric = Number(stored || '');
      if (Number.isFinite(numeric) && numeric > 0) {
        effectiveApeAmount = numeric;
        setApeAmountSol(numeric);
      }
    }
    if (!effectiveApeAmount || effectiveApeAmount <= 0) {
      setApeStatusByMint((prev) => ({
        ...prev,
        [mint]: { message: 'Set your Ape amount in Settings.', error: true }
      }));
      return;
    }

    setApeLoadingByMint((prev) => ({ ...prev, [mint]: true }));
    setApeStatusByMint((prev) => ({ ...prev, [mint]: { message: 'Submitting ape buy…' } }));
    try {
      const amountInBase = Math.floor(effectiveApeAmount * 1e9);
      const tokenPriceUsd = token.contract_address
        ? getPriceForToken(token.contract_address, SOLANA_CHAIN_ID)
        : null;
      const solPriceUsd = getPriceForToken('So11111111111111111111111111111111111111112', SOLANA_CHAIN_ID);
      let minAmountOut = '0';
      if (tokenPriceUsd && solPriceUsd) {
        const expectedTokens = (effectiveApeAmount * solPriceUsd) / tokenPriceUsd;
        const slippageMultiplier = 1 - (APE_SLIPPAGE_BPS / 10000);
        const minTokens = expectedTokens * slippageMultiplier;
        const decimals = token.decimals ?? 9;
        const minBase = Math.floor(minTokens * Math.pow(10, decimals));
        if (Number.isFinite(minBase) && minBase > 0) {
          minAmountOut = String(minBase);
        }
      }

      const response = await swapPwa({
        direction: 'buy',
        mintAddress: token.mint_address,
        poolAddress: token.pool_address,
        amountIn: String(amountInBase),
        minAmountOut
      });
      setApeStatusByMint((prev) => ({
        ...prev,
        [mint]: {
          message: 'Ape buy complete.',
          signature: response.signature,
          amountOut: response.amountOut ?? null,
          symbol: token.symbol || null
        }
      }));
    } catch (error: any) {
      const errorText = error?.message || 'Ape buy failed.';
      if (errorText.toLowerCase().includes('insufficient')) {
        setApeStatusByMint((prev) => ({
          ...prev,
          [mint]: { message: 'Insufficient Funds!', error: true }
        }));
        return;
      }
      setApeStatusByMint((prev) => ({
        ...prev,
        [mint]: { message: errorText, error: true }
      }));
    } finally {
      setApeLoadingByMint((prev) => ({ ...prev, [mint]: false }));
    }
  };

  const featurePricesPrimary: Record<4 | 8 | 12 | 24, number> = {
    4: 1,
    8: 2,
    12: 3,
    24: 5
  };

  const featurePricesSecondary: Record<4 | 8 | 12 | 24, number> = {
    4: 0.5,
    8: 1,
    12: 1.5,
    24: 2
  };

  const activeSlot = (slotType: string) => {
    if (!featuredSlots) return null;
    if (slotType === 'primary') return featuredSlots.primary;
    return featuredSlots.secondary.find((slot) => slot.slotType === slotType) || null;
  };

  const handleOpenFeatureModal = () => {
    setFeatureModalOpen(true);
    setFeatureStatus(null);
    setFeatureSearch('');
    setFeatureToken(null);
    setFeatureSlotType('primary');
    setFeatureDuration(4);
  };

  const handleSubmitFeature = async () => {
    if (!featureToken?.mint_address) {
      setFeatureStatus('Select a token to feature.');
      return;
    }
    const slotActive = activeSlot(featureSlotType);
    if (slotActive) {
      setFeatureStatus(`Slot is occupied until ${new Date(slotActive.endsAt).toLocaleString()}.`);
      return;
    }
    setFeatureLoading(true);
    setFeatureStatus(null);
    try {
      await createPwaFeaturedSlot({
        slotType: featureSlotType,
        mintAddress: featureToken.mint_address,
        durationHours: featureDuration
      });
      const next = await fetchPwaFeaturedSlots();
      setFeaturedSlots(next);
      setFeatureStatus('Featured slot purchased successfully.');
      setFeatureModalOpen(false);
    } catch (error: any) {
      setFeatureStatus(error?.message || 'Unable to feature token.');
    } finally {
      setFeatureLoading(false);
    }
  };

  const handlePurchaseCarousel = async () => {
    if (!featureToken?.mint_address) {
      setFeatureStatus('Select a token to feature.');
      return;
    }
    if (carouselSlots && carouselSlots.availableSlots <= 0) {
      setFeatureStatus('No carousel slots available.');
      return;
    }
    setCarouselLoading(true);
    setFeatureStatus(null);
    try {
      await createPwaFeaturedCarousel({ mintAddress: featureToken.mint_address });
      const next = await fetchPwaFeaturedCarousel();
      setCarouselSlots(next);
      setFeatureStatus('Carousel slot purchased successfully.');
      setFeatureModalOpen(false);
    } catch (error: any) {
      setFeatureStatus(error?.message || 'Unable to purchase carousel slot.');
    } finally {
      setCarouselLoading(false);
    }
  };

  const findTokenByMint = (mint?: string | null) => {
    if (!mint) return null;
    return tokens.find((token) =>
      token.mint_address?.toLowerCase() === mint.toLowerCase() ||
      token.contract_address?.toLowerCase() === mint.toLowerCase()
    ) || null;
  };

  const featuredToken = useMemo(() => {
    const paidPrimary = featuredSlots?.primary ? findTokenByMint(featuredSlots.primary.mintAddress) : null;
    if (paidPrimary) {
      const tokenAddress = paidPrimary.contract_address || paidPrimary.mint_address;
      const priceUSD = tokenAddress ? getPriceForToken(tokenAddress, SOLANA_CHAIN_ID) : null;
      const marketCap = priceUSD ? priceUSD * 1_000_000_000 : 0;
      return { token: paidPrimary, marketCap };
    }
    if (FEATURED_OVERRIDE_MINT) {
      const overrideToken = findTokenByMint(FEATURED_OVERRIDE_MINT);
      if (overrideToken) {
        const tokenAddress = overrideToken.contract_address || overrideToken.mint_address;
        const priceUSD = tokenAddress ? getPriceForToken(tokenAddress, SOLANA_CHAIN_ID) : null;
        const marketCap = priceUSD ? priceUSD * 1_000_000_000 : 0;
        return { token: overrideToken, marketCap };
      }
    }
    let bestToken: SolanaTokenData | null = null;
    let bestMarketCap = 0;
    tokens.forEach((token) => {
      const symbol = token.symbol?.toUpperCase() || '';
      if (symbol === SENTRY_TOKEN_SYMBOL) return;
      const tokenAddress = token.contract_address || token.mint_address;
      if (!tokenAddress) return;
      const priceUSD = getPriceForToken(tokenAddress, SOLANA_CHAIN_ID);
      if (!priceUSD) return;
      const marketCap = priceUSD * 1_000_000_000;
      if (marketCap > bestMarketCap) {
        bestMarketCap = marketCap;
        bestToken = token;
      }
    });
    return bestToken ? { token: bestToken, marketCap: bestMarketCap } : null;
  }, [tokens, getPriceForToken, featuredSlots]);

  const scrollingTokens = useMemo(() => {
    if (tokens.length === 0) return [];
    const paidCarousel = (carouselSlots?.slots || [])
      .map((slot) => findTokenByMint(slot.mintAddress))
      .filter(Boolean) as SolanaTokenData[];
    const paidSet = new Set(
      paidCarousel
        .map((token) => (token.mint_address || token.contract_address || '').toLowerCase())
        .filter(Boolean)
    );
    const fill = tokens
      .filter((token) => token.symbol?.toUpperCase() !== SENTRY_TOKEN_SYMBOL)
      .filter((token) => {
        const key = (token.mint_address || token.contract_address || '').toLowerCase();
        return key && !paidSet.has(key);
      })
      .slice(0, Math.max(0, 20 - paidCarousel.length));
    const base = [...paidCarousel, ...fill].slice(0, 20);
    if (base.length >= 3) return [...base, ...base];
    return [...base, ...base, ...base, ...base];
  }, [tokens, carouselSlots]);

  const paidCarouselSet = useMemo(() => {
    const set = new Set<string>();
    (carouselSlots?.slots || []).forEach((slot) => {
      if (slot.mintAddress) {
        set.add(slot.mintAddress.toLowerCase());
      }
    });
    return set;
  }, [carouselSlots]);

  const secondaryFeaturedTokens = useMemo(() => {
    const excludedMints = new Set<string>();
    if (featuredToken?.token.mint_address) {
      excludedMints.add(featuredToken.token.mint_address.toLowerCase());
    }
    if (featuredToken?.token.contract_address) {
      excludedMints.add(featuredToken.token.contract_address.toLowerCase());
    }
    const paidSecondary = (featuredSlots?.secondary || []).map((slot) => findTokenByMint(slot.mintAddress)).filter(Boolean) as SolanaTokenData[];
    paidSecondary.forEach((token) => {
      if (token.mint_address) excludedMints.add(token.mint_address.toLowerCase());
      if (token.contract_address) excludedMints.add(token.contract_address.toLowerCase());
    });
    const ranked = tokens
      .filter((token) => token.symbol?.toUpperCase() !== SENTRY_TOKEN_SYMBOL)
      .filter((token) => {
        const key = (token.mint_address || token.contract_address || '').toLowerCase();
        return key && !excludedMints.has(key);
      })
      .map((token) => {
        const tokenAddress = token.contract_address || token.mint_address;
        const priceUSD = tokenAddress ? getPriceForToken(tokenAddress, SOLANA_CHAIN_ID) : null;
        const marketCap = priceUSD ? priceUSD * 1_000_000_000 : 0;
        return { token, marketCap };
      })
      .sort((a, b) => b.marketCap - a.marketCap)
      .map((entry) => entry.token);

    const filled = [...paidSecondary];
    for (const token of ranked) {
      if (filled.length >= 3) break;
      filled.push(token);
    }
    return filled.slice(0, 3);
  }, [tokens, featuredToken, featuredSlots, getPriceForToken]);

  return (
    <PageContainer>
      {showPageTitle && <PageTitle>Tokens</PageTitle>}
      {featuredLayout === 'split' ? (
        <FeaturedGrid>
          <FeaturedMainCard>
            <FeaturedHeader>
              <FeaturedTitle>Featured Project</FeaturedTitle>
              <FeaturedActionButton type="button" onClick={handleOpenFeatureModal}>
                Feature your project
              </FeaturedActionButton>
            </FeaturedHeader>
            {featuredToken ? (
              <FeaturedBody>
                <TokenLogo
                  src={featuredToken.token.logo_url || '/token-placeholder.png'}
                  alt={featuredToken.token.name || 'Token'}
                  onError={(event) => {
                    (event.target as HTMLImageElement).src = '/token-placeholder.png';
                  }}
                />
                <FeaturedInfo>
                  <FeaturedName>{featuredToken.token.name || 'Unnamed Token'}</FeaturedName>
                  <FeaturedSymbol>{featuredToken.token.symbol || 'TOKEN'}</FeaturedSymbol>
                  <FeaturedMetaRow>
                    <span>Market cap: {formatMarketCapValue(featuredToken.marketCap)}</span>
                    <span>
                      Price:{' '}
                      {featuredToken.token.contract_address || featuredToken.token.mint_address
                        ? formatPriceDisplay(
                            getPriceForToken(
                              featuredToken.token.contract_address || featuredToken.token.mint_address || '',
                              SOLANA_CHAIN_ID
                            ) || 0
                          )
                        : '—'}
                    </span>
                  </FeaturedMetaRow>
                  <FeaturedActions>
                    <TradeButton
                      type="button"
                      $disabled={!featuredToken.token.pool_address}
                      onClick={() => handleTokenTrade(featuredToken.token)}
                    >
                      Trade
                    </TradeButton>
                  </FeaturedActions>
                  <FeaturedSocialRow>
                    {normalizeWebsiteUrl(featuredToken.token.website) && (
                      <TokenLink href={normalizeWebsiteUrl(featuredToken.token.website) || undefined} target="_blank" rel="noreferrer">
                        <TokenLinkIcon src={WEBSITE_ICON_URL} alt="Website" />
                      </TokenLink>
                    )}
                    {normalizeTwitterUrl(featuredToken.token.twitter) && (
                      <TokenLink href={normalizeTwitterUrl(featuredToken.token.twitter) || undefined} target="_blank" rel="noreferrer">
                        <TokenLinkIcon src={TWITTER_ICON_URL} alt="Twitter" />
                      </TokenLink>
                    )}
                    {normalizeTelegramUrl(featuredToken.token.telegram) && (
                      <TokenLink href={normalizeTelegramUrl(featuredToken.token.telegram) || undefined} target="_blank" rel="noreferrer">
                        <TokenLinkIcon src={TELEGRAM_ICON_URL} alt="Telegram" />
                      </TokenLink>
                    )}
                  </FeaturedSocialRow>
                </FeaturedInfo>
              </FeaturedBody>
            ) : (
              <HelperText>Loading featured token…</HelperText>
            )}
          </FeaturedMainCard>
          <FeaturedSideCard>
            <SecondaryFeatureRow>
              {secondaryFeaturedTokens.map((token) => (
                <SecondaryFeatureCard key={token.mint_address} type="button" onClick={() => handleTokenClick(token)}>
                  <ScrollingTokenLogo
                    src={token.logo_url || '/token-placeholder.png'}
                    alt={token.symbol || 'Token'}
                    onError={(event) => {
                      (event.target as HTMLImageElement).src = '/token-placeholder.png';
                    }}
                  />
                  <SecondaryFeatureSymbol>{token.symbol || 'TOKEN'}</SecondaryFeatureSymbol>
                </SecondaryFeatureCard>
              ))}
            </SecondaryFeatureRow>
            <ScrollingFeedContainerCompact>
              <ScrollingFeedTrack>
                {scrollingTokens.map((token, index) => {
                  const tokenAddress = token.contract_address || token.mint_address;
                  const priceUSD = tokenAddress ? getPriceForToken(tokenAddress, SOLANA_CHAIN_ID) : null;
                  const marketCap = priceUSD ? formatMarketCapValue(priceUSD * 1_000_000_000) : '—';
                  const key = (token.mint_address || token.contract_address || '').toLowerCase();
                  const isPaidCarousel = key ? paidCarouselSet.has(key) : false;
                  return (
                    <ScrollingTokenCard
                      key={`${token.mint_address}-${index}`}
                      $featured={isPaidCarousel}
                      onClick={() => handleTokenClick(token)}
                    >
                      <ScrollingTokenLogo
                        src={token.logo_url || '/token-placeholder.png'}
                        alt={token.symbol || 'Token'}
                        onError={(event) => {
                          (event.target as HTMLImageElement).src = '/token-placeholder.png';
                        }}
                      />
                      <ScrollingTokenInfo>
                        <ScrollingTokenSymbol>{token.symbol || 'TOKEN'}</ScrollingTokenSymbol>
                      </ScrollingTokenInfo>
                      <ScrollingTokenMarketCap>{marketCap}</ScrollingTokenMarketCap>
                    </ScrollingTokenCard>
                  );
                })}
              </ScrollingFeedTrack>
            </ScrollingFeedContainerCompact>
          </FeaturedSideCard>
        </FeaturedGrid>
      ) : (
        <>
          <FeaturedCard>
            <FeaturedHeader>
              <FeaturedTitle>Featured Project</FeaturedTitle>
              <FeaturedActionButton type="button" onClick={handleOpenFeatureModal}>
                Feature your project
              </FeaturedActionButton>
            </FeaturedHeader>
            {featuredToken ? (
              <FeaturedBody>
                <TokenLogo
                  src={featuredToken.token.logo_url || '/token-placeholder.png'}
                  alt={featuredToken.token.name || 'Token'}
                  onError={(event) => {
                    (event.target as HTMLImageElement).src = '/token-placeholder.png';
                  }}
                />
                <FeaturedInfo>
                  <FeaturedName>{featuredToken.token.name || 'Unnamed Token'}</FeaturedName>
                  <FeaturedSymbol>{featuredToken.token.symbol || 'TOKEN'}</FeaturedSymbol>
                  <FeaturedMetaRow>
                    <span>Market cap: {formatMarketCapValue(featuredToken.marketCap)}</span>
                    <span>
                      Price:{' '}
                      {featuredToken.token.contract_address || featuredToken.token.mint_address
                        ? formatPriceDisplay(
                            getPriceForToken(
                              featuredToken.token.contract_address || featuredToken.token.mint_address || '',
                              SOLANA_CHAIN_ID
                            ) || 0
                          )
                        : '—'}
                    </span>
                  </FeaturedMetaRow>
                  <FeaturedActions>
                    <TradeButton
                      type="button"
                      $disabled={!featuredToken.token.pool_address}
                      onClick={() => handleTokenTrade(featuredToken.token)}
                    >
                      Trade
                    </TradeButton>
                  </FeaturedActions>
                  <FeaturedSocialRow>
                    {normalizeWebsiteUrl(featuredToken.token.website) && (
                      <TokenLink href={normalizeWebsiteUrl(featuredToken.token.website) || undefined} target="_blank" rel="noreferrer">
                        <TokenLinkIcon src={WEBSITE_ICON_URL} alt="Website" />
                      </TokenLink>
                    )}
                    {normalizeTwitterUrl(featuredToken.token.twitter) && (
                      <TokenLink href={normalizeTwitterUrl(featuredToken.token.twitter) || undefined} target="_blank" rel="noreferrer">
                        <TokenLinkIcon src={TWITTER_ICON_URL} alt="Twitter" />
                      </TokenLink>
                    )}
                    {normalizeTelegramUrl(featuredToken.token.telegram) && (
                      <TokenLink href={normalizeTelegramUrl(featuredToken.token.telegram) || undefined} target="_blank" rel="noreferrer">
                        <TokenLinkIcon src={TELEGRAM_ICON_URL} alt="Telegram" />
                      </TokenLink>
                    )}
                  </FeaturedSocialRow>
                </FeaturedInfo>
              </FeaturedBody>
            ) : (
              <HelperText>Loading featured token…</HelperText>
            )}
          </FeaturedCard>
          <SecondaryFeatureRow>
            {secondaryFeaturedTokens.map((token) => (
              <SecondaryFeatureCard key={token.mint_address} type="button" onClick={() => handleTokenClick(token)}>
                <ScrollingTokenLogo
                  src={token.logo_url || '/token-placeholder.png'}
                  alt={token.symbol || 'Token'}
                  onError={(event) => {
                    (event.target as HTMLImageElement).src = '/token-placeholder.png';
                  }}
                />
                <SecondaryFeatureSymbol>{token.symbol || 'TOKEN'}</SecondaryFeatureSymbol>
              </SecondaryFeatureCard>
            ))}
          </SecondaryFeatureRow>
          <ScrollingFeedContainer>
            <ScrollingFeedTrack>
              {scrollingTokens.map((token, index) => {
                const tokenAddress = token.contract_address || token.mint_address;
                const priceUSD = tokenAddress ? getPriceForToken(tokenAddress, SOLANA_CHAIN_ID) : null;
                const marketCap = priceUSD ? formatMarketCapValue(priceUSD * 1_000_000_000) : '—';
                const key = (token.mint_address || token.contract_address || '').toLowerCase();
                const isPaidCarousel = key ? paidCarouselSet.has(key) : false;
                return (
                  <ScrollingTokenCard
                    key={`${token.mint_address}-${index}`}
                    $featured={isPaidCarousel}
                    onClick={() => handleTokenClick(token)}
                  >
                    <ScrollingTokenLogo
                      src={token.logo_url || '/token-placeholder.png'}
                      alt={token.symbol || 'Token'}
                      onError={(event) => {
                        (event.target as HTMLImageElement).src = '/token-placeholder.png';
                      }}
                    />
                    <ScrollingTokenInfo>
                      <ScrollingTokenSymbol>{token.symbol || 'TOKEN'}</ScrollingTokenSymbol>
                    </ScrollingTokenInfo>
                    <ScrollingTokenMarketCap>{marketCap}</ScrollingTokenMarketCap>
                  </ScrollingTokenCard>
                );
              })}
            </ScrollingFeedTrack>
          </ScrollingFeedContainer>
        </>
      )}
      <SearchRow>
        <SearchInput
          type="search"
          placeholder="Search tokens..."
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </SearchRow>
      {loading && <HelperText>Loading tokens…</HelperText>}
      {!loading && error && <HelperText>{error}</HelperText>}
      {!loading && !error && (
        <TokenGrid>
          {filteredTokens.map((token) => {
            const tokenAddress = token.contract_address || token.mint_address;
            const priceUSD = tokenAddress ? getPriceForToken(tokenAddress, SOLANA_CHAIN_ID) : null;
            const change24h = tokenAddress ? get24hChangeForToken(tokenAddress, SOLANA_CHAIN_ID) : null;
            const marketCap = priceUSD ? formatMarketCapValue(priceUSD * 1_000_000_000) : '—';
            const websiteUrl = normalizeWebsiteUrl(token.website);
            const twitterUrl = normalizeTwitterUrl(token.twitter);
            const telegramUrl = normalizeTelegramUrl(token.telegram);
            const dexscreenerUrl = tokenAddress ? `https://dexscreener.com/solana/${tokenAddress}` : null;
            const orcaUrl = tokenAddress
              ? `https://www.orca.so/?tokenIn=So11111111111111111111111111111111111111112&tokenOut=${encodeURIComponent(tokenAddress)}`
              : null;
            const creatorLabel = token.creator_username ? `@${token.creator_username}` : '@anondev';

            return (
              <TokenCard key={token.mint_address} onClick={() => handleOpenChart(token)}>
                <TokenHeader>
                  <TokenLogo
                    src={token.logo_url || '/token-placeholder.png'}
                    alt={token.name || 'Token'}
                    onError={(event) => {
                      (event.target as HTMLImageElement).src = '/token-placeholder.png';
                    }}
                  />
                  <TokenInfo>
                    <TokenName>{token.name || 'Unnamed Token'}</TokenName>
                    <TokenSymbol>{token.symbol || 'TOKEN'}</TokenSymbol>
                    <TokenCreator>{creatorLabel}</TokenCreator>
                  </TokenInfo>
                  <TokenActionColumn>
                    <ApeButton
                      type="button"
                      $disabled={!token.pool_address || apeLoadingByMint[token.mint_address]}
                      onClick={(event) => {
                        event.stopPropagation();
                        handleApeClick(token);
                      }}
                    >
                      {apeLoadingByMint[token.mint_address] ? 'Ape…' : 'Ape'}
                    </ApeButton>
                    <TradeButton
                      type="button"
                      $disabled={!token.pool_address}
                      onClick={(event) => {
                        event.stopPropagation();
                        handleTokenTrade(token);
                      }}
                    >
                      Trade
                    </TradeButton>
                    {apeStatusByMint[token.mint_address] && (
                      <ApeStatusText $error={apeStatusByMint[token.mint_address].error}>
                        {apeStatusByMint[token.mint_address].signature ? (
                          <>
                            Bought{' '}
                            {(apeStatusByMint[token.mint_address].amountOut ?? 0).toLocaleString('en-US', { maximumFractionDigits: 6 })}{' '}
                            {apeStatusByMint[token.mint_address].symbol || 'tokens'} ·{' '}
                            <a
                              href={`https://solscan.io/tx/${apeStatusByMint[token.mint_address].signature}`}
                              target="_blank"
                              rel="noreferrer"
                            >
                              View on Solscan
                            </a>
                          </>
                        ) : (
                          apeStatusByMint[token.mint_address].message
                        )}
                      </ApeStatusText>
                    )}
                  </TokenActionColumn>
                </TokenHeader>
                <TokenStatsRow>
                  <TokenStat>
                    <TokenStatLabel>Price</TokenStatLabel>
                    <TokenStatValue>{priceUSD ? formatPriceDisplay(priceUSD) : '—'}</TokenStatValue>
                    <PriceChange $positive={(change24h || 0) >= 0}>
                      {formatPriceChange(change24h)}
                    </PriceChange>
                  </TokenStat>
                  <TokenStat>
                    <TokenStatLabel>Market cap</TokenStatLabel>
                    <TokenStatValue>{marketCap}</TokenStatValue>
                  </TokenStat>
                </TokenStatsRow>
                <LinksRow>
                  {websiteUrl && (
                    <TokenLink
                      href={websiteUrl}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(event) => event.stopPropagation()}
                    >
                      <TokenLinkIcon src={WEBSITE_ICON_URL} alt="Website" />
                    </TokenLink>
                  )}
                  {dexscreenerUrl && (
                    <TokenLink
                      href={dexscreenerUrl}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(event) => event.stopPropagation()}
                    >
                      <TokenLinkIcon src={DEXSCREENER_ICON_URL} alt="Dexscreener" />
                    </TokenLink>
                  )}
                  {orcaUrl && (
                    <TokenLink
                      href={orcaUrl}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(event) => event.stopPropagation()}
                    >
                      <TokenLinkIcon src={ORCA_ICON_URL} alt="Orca" />
                    </TokenLink>
                  )}
                  {twitterUrl && (
                    <TokenLink
                      href={twitterUrl}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(event) => event.stopPropagation()}
                    >
                      <TokenLinkIcon src={TWITTER_ICON_URL} alt="Twitter" />
                    </TokenLink>
                  )}
                  {telegramUrl && (
                    <TokenLink
                      href={telegramUrl}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(event) => event.stopPropagation()}
                    >
                      <TokenLinkIcon src={TELEGRAM_ICON_URL} alt="Telegram" />
                    </TokenLink>
                  )}
                </LinksRow>
                {token.is_agent && (
                  <AgentNotice>
                    <span>This token was created by a verified OpenClaw agent. 🦞</span>
                    <AgentNoticeIcon src={VERYAI_ICON_URL} alt="VeryAI" />
                  </AgentNotice>
                )}
              </TokenCard>
            );
          })}
        </TokenGrid>
      )}
      {featureModalOpen && (
        <ModalBackdrop onClick={() => setFeatureModalOpen(false)}>
          <ModalCard onClick={(event) => event.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Feature your project</ModalTitle>
              <CloseButton type="button" onClick={() => setFeatureModalOpen(false)}>×</CloseButton>
            </ModalHeader>
            <ModalSearchInput
              type="search"
              placeholder="Search for your token"
              value={featureSearch}
              onChange={(event) => setFeatureSearch(event.target.value)}
            />
            {featureToken && (
              <SelectedTokenRow>
                <ScrollingTokenLogo
                  src={featureToken.logo_url || '/token-placeholder.png'}
                  alt={featureToken.symbol || 'Token'}
                  onError={(event) => {
                    (event.target as HTMLImageElement).src = '/token-placeholder.png';
                  }}
                />
                <ModalTokenInfo>
                  <div>{featureToken.name || 'Unnamed Token'}</div>
                  <HelperText>{featureToken.symbol || 'TOKEN'}</HelperText>
                </ModalTokenInfo>
              </SelectedTokenRow>
            )}
            {featureSearch.trim().length > 0 && !featureToken ? (
              <ModalTokenList>
                {featureSearchResults.map((token) => (
                  <ModalTokenButton
                    key={token.mint_address}
                    type="button"
                    $active={featureToken?.mint_address === token.mint_address}
                    onClick={() => {
                      setFeatureToken(token);
                      setFeatureSearch('');
                    }}
                  >
                    <ScrollingTokenLogo
                      src={token.logo_url || '/token-placeholder.png'}
                      alt={token.symbol || 'Token'}
                      onError={(event) => {
                        (event.target as HTMLImageElement).src = '/token-placeholder.png';
                      }}
                    />
                    <ModalTokenInfo>
                      <div>{token.name || 'Unnamed Token'}</div>
                      <HelperText>{token.symbol || 'TOKEN'}</HelperText>
                    </ModalTokenInfo>
                  </ModalTokenButton>
                ))}
              </ModalTokenList>
            ) : null}
            <HelperText>Select slot</HelperText>
            <SlotPickerWrapper>
              <SlotPickerPrimary
                type="button"
                $active={featureSlotType === 'primary'}
                onClick={() => setFeatureSlotType('primary')}
              >
                {featureSlotType === 'primary' && featureToken ? (
                  <ScrollingTokenLogo
                    src={featureToken.logo_url || '/token-placeholder.png'}
                    alt={featureToken.symbol || 'Token'}
                    onError={(event) => {
                      (event.target as HTMLImageElement).src = '/token-placeholder.png';
                    }}
                  />
                ) : (
                  <SlotPlaceholder />
                )}
                <SlotPickerLabel>Featured Project</SlotPickerLabel>
              </SlotPickerPrimary>
              <SlotPickerSecondaryRow>
                {(['secondary_1', 'secondary_2', 'secondary_3'] as const).map((slot, index) => (
                  <SlotPickerSecondary
                    key={slot}
                    type="button"
                    $active={featureSlotType === slot}
                    onClick={() => setFeatureSlotType(slot)}
                  >
                    {featureSlotType === slot && featureToken ? (
                      <ScrollingTokenLogo
                        src={featureToken.logo_url || '/token-placeholder.png'}
                        alt={featureToken.symbol || 'Token'}
                        onError={(event) => {
                          (event.target as HTMLImageElement).src = '/token-placeholder.png';
                        }}
                      />
                    ) : (
                      <SlotPlaceholder />
                    )}
                    <SlotPickerLabel>Spot {index + 1}</SlotPickerLabel>
                  </SlotPickerSecondary>
                ))}
              </SlotPickerSecondaryRow>
            </SlotPickerWrapper>
            <HelperText>Select duration</HelperText>
            <SlotOptionRow>
              {[4, 8, 12, 24].map((hours) => (
                <SlotOptionButton
                  key={hours}
                  type="button"
                  $active={featureDuration === hours}
                  onClick={() => setFeatureDuration(hours as 4 | 8 | 12 | 24)}
                >
                  {hours}h
                </SlotOptionButton>
              ))}
            </SlotOptionRow>
            <HelperText>
              Price: {featureSlotType === 'primary'
                ? `${featurePricesPrimary[featureDuration]} SOL`
                : `${featurePricesSecondary[featureDuration]} SOL`}
            </HelperText>
            <HelperText>
              Carousel slots available: {carouselSlots?.availableSlots ?? 0}/{carouselSlots?.totalSlots ?? 20}
            </HelperText>
            {featureStatus && <HelperText>{featureStatus}</HelperText>}
            <PayButton type="button" onClick={handleSubmitFeature} disabled={featureLoading}>
              {featureLoading ? 'Processing…' : 'Pay & Feature'}
            </PayButton>
            <PayButton type="button" onClick={handlePurchaseCarousel} disabled={carouselLoading}>
              {carouselLoading ? 'Processing…' : `Buy Carousel Slot (${FEATURED_CAROUSEL_PRICE_SOL} SOL)`}
            </PayButton>
          </ModalCard>
        </ModalBackdrop>
      )}
      {chartModalOpen && chartToken && (
        <ModalBackdrop onClick={() => setChartModalOpen(false)}>
          <ChartModalCard onClick={(event) => event.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Token Chart</ModalTitle>
              <CloseButton type="button" onClick={() => setChartModalOpen(false)}>×</CloseButton>
            </ModalHeader>
            <ChartHeaderRow>
              <ChartTokenLogo
                src={chartToken.logo_url || '/token-placeholder.png'}
                alt={chartToken.name || 'Token'}
                onError={(event) => {
                  (event.target as HTMLImageElement).src = '/token-placeholder.png';
                }}
              />
              <ChartTokenInfo>
                <ChartTokenName>{chartToken.name || 'Unnamed Token'}</ChartTokenName>
                <ChartTokenSymbol>{chartToken.symbol || 'TOKEN'}</ChartTokenSymbol>
              </ChartTokenInfo>
            </ChartHeaderRow>
            <ChartTimeframeRow>
              {(['1m', '5m', '15m', '30m', '1h', '4h', '1d'] as const).map((value) => (
                <ChartTimeframeButton
                  key={value}
                  type="button"
                  $active={chartTimeframe === value}
                  onClick={() => setChartTimeframe(value)}
                >
                  {value}
                </ChartTimeframeButton>
              ))}
            </ChartTimeframeRow>
            <ChartFrameWrapper>
              <ChartCanvas ref={chartContainerRef} />
              {!chartToken.pool_address && (
                <ChartPlaceholder>No pool address available.</ChartPlaceholder>
              )}
              {chartToken.pool_address && chartDataStatus && (
                <ChartPlaceholder>{chartDataStatus}</ChartPlaceholder>
              )}
            </ChartFrameWrapper>
            <ChartToggleRow>
              <ChartToggleButton
                type="button"
                $variant="sol"
                $active={chartSide === 'sol'}
                onClick={() => setChartSide('sol')}
              >
                SOL
              </ChartToggleButton>
              <ChartToggleButton
                type="button"
                $variant="token"
                $active={chartSide === 'token'}
                onClick={() => setChartSide('token')}
              >
                {chartToken.symbol || 'TOKEN'}
              </ChartToggleButton>
            </ChartToggleRow>
            <ChartInputRow>
              <ChartInput
                type="text"
                placeholder={`Enter ${chartSide === 'sol' ? 'SOL' : chartToken.symbol || 'TOKEN'} amount`}
                value={chartAmount}
                onChange={(event) => handleChartAmountChange(event.target.value)}
              />
              <ChartBalanceRow>
                {chartSide === 'sol' ? (
                  <>
                    {(() => {
                      const availableSol = chartWalletBalance?.balanceSol ?? 0;
                      const solPrice =
                        chartSolPriceUsd ??
                        getPriceForToken('So11111111111111111111111111111111111111112', SOLANA_CHAIN_ID);
                      const usdValue = solPrice ? availableSol * solPrice : null;
                      return (
                        <>
                          Available: {availableSol.toLocaleString('en-US', { maximumFractionDigits: 4 })} SOL ( $
                          {usdValue !== null ? usdValue.toFixed(2) : '—'} )
                        </>
                      );
                    })()}
                  </>
                ) : (
                  <>
                    {(() => {
                      const availableToken = chartTokenBalance?.amount ?? 0;
                      const tokenAddress = chartToken.contract_address || chartToken.mint_address;
                      const tokenPrice =
                        chartTokenPriceUsd ?? (tokenAddress ? getPriceForToken(tokenAddress, SOLANA_CHAIN_ID) : null);
                      const usdValue = tokenPrice ? availableToken * tokenPrice : null;
                      return (
                        <>
                          Available: {availableToken.toLocaleString('en-US', { maximumFractionDigits: 4 })} {chartToken.symbol || 'TOKEN'} ( $
                          {usdValue !== null ? usdValue.toFixed(2) : '—'} )
                        </>
                      );
                    })()}
                  </>
                )}
              </ChartBalanceRow>
            </ChartInputRow>
            {chartStatus && <HelperText>{chartStatus}</HelperText>}
            <ChartActionsRow>
              <ChartActionButton
                type="button"
                $variant="buy"
                $disabled={chartLoading || chartSide !== 'sol'}
                onClick={() => handleChartSwap('buy')}
              >
                {chartLoading && chartSide === 'sol' ? 'Processing…' : 'Buy'}
              </ChartActionButton>
              <ChartActionButton
                type="button"
                $variant="sell"
                $disabled={chartLoading || chartSide !== 'token'}
                onClick={() => handleChartSwap('sell')}
              >
                {chartLoading && chartSide === 'token' ? 'Processing…' : 'Sell'}
              </ChartActionButton>
            </ChartActionsRow>
          </ChartModalCard>
        </ModalBackdrop>
      )}
    </PageContainer>
  );
};

export default SentryTokensPWA;
